<template>
  <div class="musubi-page musubi-page--visitors">
    <header class="musubi-page__head">
      <h1>Website Visitors</h1>
      <p class="text-muted">
        Companies we could identify from the traffic hitting your website — matched to firms in your
        corpus so you can follow up while intent is fresh. Add a promising visitor straight into a
        pipeline or drip flow.
      </p>
    </header>

    <!-- ── Gated (Starter) teaser: violet plan-gate. Skeleton rows (never
         fabricated data) blur behind the upgrade panel. `gateLocked` is null
         while /me/usage loads (render nothing yet). ── -->
    <div v-if="gateLocked" class="viz-teaser">
      <div class="viz-teaser__blur" aria-hidden="true">
        <div class="viz-skel-tbl">
          <div v-for="n in 5" :key="n" class="viz-skel-row">
            <span class="skel" style="width:38%"></span>
            <span class="skel" style="width:14%"></span>
            <span class="skel" style="width:12%"></span>
            <span class="skel" style="width:20%"></span>
          </div>
        </div>
      </div>
      <div class="viz-teaser__panel">
        <div class="upgrade">
          <div class="upgrade__ic"><CygnusIcon name="award" /></div>
          <div class="upgrade__eyebrow">Available on Growth</div>
          <h3>See who's visiting your website</h3>
          <p>
            Website Visitors identifies the companies browsing your site and links them to your
            prospect corpus. It's included on the <strong>Growth</strong> plan.
          </p>
          <div class="upgrade__benefits">
            <div class="benefit"><CygnusIcon name="check" /> Identify visiting companies by email engagement and network signals</div>
            <div class="benefit"><CygnusIcon name="check" /> See each visitor's page-by-page journey across sessions</div>
            <div class="benefit"><CygnusIcon name="check" /> Auto-trigger drip flows and pipelines from a qualified visit</div>
          </div>
          <div class="upgrade__actions">
            <router-link to="/admin/usage-plans" class="btn viz-btn-violet">Upgrade to Growth</router-link>
            <router-link to="/admin/usage-plans" class="viz-compare">Compare plans <CygnusIcon name="arrow-right" /></router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Entitled page ── -->
    <template v-else-if="gateLocked === false">
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

      <div class="coverage-note">
        <CygnusIcon name="info-circle" />
        <span>
          <strong>{{ kpis.identified_visits_7d }} identified company visits this week.</strong>
          Only a portion of business traffic can be identified — self-serve matching typically resolves
          <strong>5–15%</strong> of visits to a company, and never individual consumers on home broadband
          or mobile. Treat this as a sample of who's looking, not a complete visitor log.
        </span>
      </div>

      <section class="viz-kpis">
        <MusubiKpiCard label="Identified visits · 7d" :value="kpis.identified_visits_7d" icon="eye" />
        <MusubiKpiCard label="New companies" :value="kpis.new_companies" delta="first seen this week" icon="plus" />
        <MusubiKpiCard label="High confidence" :value="kpis.high_confidence"
          :delta="highPct" delta-dir="neutral" icon="award" />
        <MusubiKpiCard label="Added to pipeline" :value="kpis.added_to_pipeline" icon="diagram" />
      </section>

      <div class="viz-tabs">
        <button v-for="t in tabs" :key="t.key" class="viz-tab" :class="{ active: tab === t.key }" @click="setTab(t.key)">
          {{ t.label }}<span v-if="counts[t.countKey]" class="count">{{ counts[t.countKey] }}</span>
        </button>
      </div>

      <!-- Awaiting-review posture banner -->
      <div v-if="tab === 'awaiting'" class="viz-banner">
        <CygnusIcon name="warning" />
        <div>
          <strong>Auto-enrol is off by default.</strong> New identified visitors land here for a human look
          before any outreach. A single anonymous website visit isn't consent to email — review, then add
          the ones worth pursuing. You can turn on auto-enrol per drip flow once you trust the matching.
        </div>
      </div>

      <!-- Bulk bar (review tab) -->
      <div v-if="tab === 'awaiting' && selectedIds.length" class="viz-bulkbar">
        <span class="viz-bulkbar__sel">{{ selectedIds.length }} selected</span>
        <div class="spacer"></div>
        <button class="btn viz-btn-primary btn-sm" :disabled="busy" @click="bulkAction('pipeline')">
          <CygnusIcon name="plus" /> Add selected to pipeline
        </button>
        <select v-model="bulkFlowId" class="viz-select viz-select--sm">
          <option :value="null">Enrol in drip flow…</option>
          <option v-for="f in activeFlows" :key="f.id" :value="f.id">{{ f.name }}</option>
        </select>
        <button class="btn viz-btn-secondary btn-sm" :disabled="busy || !bulkFlowId" @click="bulkAction('pipeline', bulkFlowId)">Enrol</button>
        <button class="btn viz-btn-secondary btn-sm" :disabled="busy" @click="bulkAction('dismiss')">Dismiss — not relevant</button>
      </div>

      <!-- Toolbar -->
      <div class="viz-toolbar">
        <select v-model="filters.confidence" class="viz-select" @change="load">
          <option value="">All confidence</option>
          <option value="high">High only</option>
          <option value="high_medium">High + Medium</option>
        </select>
        <select v-model="filters.method" class="viz-select" @change="load">
          <option value="">Any method</option>
          <option value="learned">Email engagement</option>
          <option value="rdns">Network (rDNS)</option>
          <option value="asn">Network owner (ASN)</option>
        </select>
        <select v-model="filters.sector" class="viz-select" @change="load">
          <option value="">Any sector</option>
          <option v-for="s in sectorOptions" :key="s" :value="s">{{ s }}</option>
        </select>
        <input v-model="filters.search" class="viz-input" placeholder="Search company…" style="width:180px" @keyup.enter="load" @blur="load">
        <div class="spacer"></div>
        <button class="btn viz-btn-secondary btn-sm" :disabled="exporting" @click="exportCsv">
          <CygnusIcon name="download" /> Export
        </button>
      </div>

      <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

      <!-- Table -->
      <div v-if="loading" class="viz-loading">Loading identified visitors…</div>

      <div v-else-if="!rows.length" class="dc-empty">
        <div class="dc-empty__icon"><CygnusIcon name="globe" /></div>
        <div class="dc-empty__title">No identified visitors yet</div>
        <div class="dc-empty__hint">
          Your tracking pixel is installed and receiving page views — we just haven't been able to match a
          visit to a company yet. This is normal in the first days.
        </div>
        <div class="dc-empty__why">
          <div class="row"><CygnusIcon name="clock" /> Identification builds up over time — the more email engagement and traffic you have, the more visits we can resolve.</div>
          <div class="row"><CygnusIcon name="server" /> Only business networks are identifiable. Visitors on home broadband, mobile data or VPNs can't be attributed to a company.</div>
          <div class="row"><CygnusIcon name="award" /> Self-serve matching resolves roughly 5–15% of business visits — a strong sample, not full coverage.</div>
        </div>
        <div class="dc-empty__actions">
          <router-link to="/admin/tracking" class="btn viz-btn-outline"><CygnusIcon name="check" /> Check pixel installation</router-link>
          <router-link to="/admin/tracking" class="btn viz-btn-secondary">View tracking setup</router-link>
        </div>
      </div>

      <div v-else class="viz-tbl">
        <table>
          <thead>
            <tr>
              <th v-if="tab === 'awaiting'" class="checkcell"><input type="checkbox" :checked="allSelected" @change="toggleAll($event)"></th>
              <th style="width:30px"></th>
              <th>Company</th>
              <th>Sector</th>
              <th>Confidence</th>
              <th>How identified</th>
              <th style="text-align:right">Pages</th>
              <th>Last visit</th>
              <th style="text-align:right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in rows" :key="r.company_id">
              <tr :class="{ 'is-open': expanded === r.company_id }">
                <td v-if="tab === 'awaiting'" class="checkcell">
                  <input type="checkbox" :checked="!!selectedMap[r.company_id]" @change="toggleOne(r.company_id)">
                </td>
                <td><button class="chev" @click="toggleExpand(r.company_id)"><CygnusIcon name="chevron-right" /></button></td>
                <td>
                  <div class="co-name">{{ r.name }}</div>
                  <div v-if="r.website" class="co-web"><CygnusIcon name="globe" />{{ hostOf(r.website) }}</div>
                </td>
                <td><span v-if="r.sector" class="chip sector">{{ r.sector }}</span><span v-else class="text-muted small">—</span></td>
                <td>
                  <span class="conf" :class="r.confidence">
                    <span class="dot"></span>{{ r.confidence === 'high' ? 'High' : 'Medium' }}
                  </span>
                </td>
                <td>
                  <span class="method" :title="methodTip(r.method)">
                    <CygnusIcon :name="methodIcon(r.method)" />{{ methodLabel(r.method) }}
                  </span>
                </td>
                <td style="text-align:right"><span class="pv-count">{{ r.pages }}</span></td>
                <td class="recency"><CygnusIcon name="clock" />{{ relTime(r.last_visit) }}</td>
                <td>
                  <div class="row-actions">
                    <button class="btn viz-btn-secondary btn-sm" @click="toggleExpand(r.company_id)">View pages</button>
                    <button v-if="r.review_status !== 'in_pipeline'" class="btn viz-btn-primary btn-sm" :disabled="busy" @click="addToPipeline(r.company_id)">
                      <CygnusIcon name="plus" /> Add to pipeline
                    </button>
                    <span v-else class="viz-inpipe"><CygnusIcon name="check" /> In pipeline</span>
                  </div>
                </td>
              </tr>

              <!-- Drill-down -->
              <tr v-if="expanded === r.company_id" class="drill">
                <td :colspan="tab === 'awaiting' ? 9 : 8" style="padding:0">
                  <div v-if="drillLoading" class="drill__loading">Loading visit detail…</div>
                  <div v-else-if="drill" class="drill__inner">
                    <div>
                      <h4>Visit timeline · {{ drill.sessions.length }} session{{ drill.sessions.length === 1 ? '' : 's' }}</h4>
                      <div v-for="(s, si) in drill.sessions" :key="s.id" class="tl-block">
                        <div class="tl-session">Session {{ drill.sessions.length - si }} — {{ sessionRange(s) }} · {{ s.events.length || s.page_view_count }} pages</div>
                        <div class="timeline">
                          <div v-for="(ev, ei) in s.events" :key="ei" class="tl-item" :class="{ 'session-start': ei === 0 }">
                            <div class="tl-page">{{ pagePath(ev.url) }}</div>
                            <div class="tl-meta">{{ fmtClock(ev.created) }}<span v-if="ei === 0 && s.first_referrer"> · from {{ hostOf(s.first_referrer) }}</span></div>
                          </div>
                          <div v-if="!s.events.length" class="tl-item"><div class="tl-meta">No page events recorded for this session.</div></div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4>Why we think this is {{ r.name }}</h4>
                      <div class="explain">
                        <div class="lead"><CygnusIcon name="check-circle" />{{ drill.method_label }}</div>
                        {{ drill.explanation }}
                      </div>
                      <h4>Corpus facts</h4>
                      <div class="facts">
                        <div class="fact"><div class="fact__k">Website framework</div><div class="fact__v corpus">{{ drill.facts.framework || '—' }} <span v-if="drill.facts.framework" class="corpus-tag">corpus</span></div></div>
                        <div class="fact"><div class="fact__k">Employees</div><div class="fact__v">{{ drill.facts.employees || '—' }}</div></div>
                        <div class="fact"><div class="fact__k">Region</div><div class="fact__v">{{ drill.facts.region || '—' }}</div></div>
                        <div class="fact"><div class="fact__k">Sector</div><div class="fact__v">{{ (drill.facts.sectors[0] && drill.facts.sectors[0].label) || '—' }}</div></div>
                      </div>
                      <div class="drill__cta">
                        <button v-if="r.review_status !== 'in_pipeline'" class="btn viz-btn-primary" :disabled="busy" @click="addToPipeline(r.company_id)">
                          <CygnusIcon name="plus" /> Add to pipeline
                        </button>
                        <template v-if="r.review_status !== 'in_pipeline'">
                          <select v-model="drillFlowId" class="viz-select viz-select--sm">
                            <option :value="null">Enrol in drip flow…</option>
                            <option v-for="f in activeFlows" :key="f.id" :value="f.id">{{ f.name }}</option>
                          </select>
                          <button class="btn viz-btn-outline" :disabled="busy || !drillFlowId" @click="addToPipeline(r.company_id, drillFlowId)">Enrol</button>
                        </template>
                        <button class="btn btn-ghost btn-sm" :disabled="busy" @click="dismiss(r.company_id)">Not relevant</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiKpiCard from "@root/components/ui/MusubiKpiCard.vue";

/**
 * AdminWebsiteVisitors — the tenant-facing Website Visitors page (Visitor-ID
 * slice 2). Company-grouped list of identified visitors with a review-queue
 * lifecycle (Awaiting review / In pipeline / Dismissed), an expandable
 * cross-session drill-down (page timeline + plain-English match explanation +
 * corpus facts), and commit actions (Add to pipeline / Enrol in drip flow) that
 * reuse the prospect + drip enrolment path. A Starter tenant sees the violet
 * Growth teaser instead of the list (server 403s the read anyway — this is the
 * honest UI, matching the AdminLeads Enterprise gate).
 */
export default {
  name: "AdminWebsiteVisitors",
  components: { MusubiKpiCard },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      gateLocked: null, // null=loading, true=teaser, false=entitled
      loading: false,
      busy: false,
      exporting: false,
      error: null,
      message: "",
      tab: "all",
      tabs: [
        { key: "all", label: "All visitors", countKey: "all" },
        { key: "awaiting", label: "Awaiting review", countKey: "awaiting" },
        { key: "in_pipeline", label: "In pipeline", countKey: "in_pipeline" },
        { key: "dismissed", label: "Dismissed", countKey: "dismissed" },
      ],
      filters: { confidence: "", method: "", sector: "", search: "" },
      kpis: { identified_visits_7d: 0, new_companies: 0, high_confidence: 0, added_to_pipeline: 0 },
      counts: { all: 0, awaiting: 0, in_pipeline: 0, dismissed: 0 },
      rows: [],
      total: 0,
      expanded: null,
      drill: null,
      drillLoading: false,
      drillFlowId: null,
      selectedMap: {},
      bulkFlowId: null,
      flows: [],
    };
  },
  computed: {
    highPct() {
      const t = this.kpis.identified_visits_7d;
      return t > 0 ? Math.round((this.kpis.high_confidence / Math.max(1, this.counts.all)) * 100) + "% of identified" : "";
    },
    selectedIds() {
      return Object.keys(this.selectedMap).filter((k) => this.selectedMap[k]).map(Number);
    },
    allSelected() {
      return this.rows.length > 0 && this.rows.every((r) => this.selectedMap[r.company_id]);
    },
    sectorOptions() {
      return [...new Set(this.rows.map((r) => r.sector).filter(Boolean))].sort();
    },
    activeFlows() {
      return this.flows.filter((f) => f.status === "active" || f.status === "draft");
    },
  },
  created() {
    this.checkGate();
  },
  methods: {
    async checkGate() {
      try {
        const res = await this.api.get("/api/v1/me/usage");
        const entitled = !!res?.features?.visitor_id || !!res?.plan?.can_manage;
        this.gateLocked = !entitled;
      } catch (e) {
        // Fail-open: a usage blip shouldn't lock an entitled tenant out. The
        // server-side gate is still authoritative on every data read.
        this.gateLocked = false;
      }
      if (this.gateLocked === false) {
        this.loadFlows();
        this.load();
      }
    },
    async load() {
      this.loading = true;
      this.error = null;
      this.expanded = null;
      try {
        const conf = this.filters.confidence === "high_medium" ? "" : this.filters.confidence;
        const qs = new URLSearchParams({
          tab: this.tab,
          confidence: conf,
          method: this.filters.method,
          sector: this.filters.sector,
          search: this.filters.search,
          per_page: "100",
        });
        const res = await this.api.get("/api/v1/admin/visitor-id/matches?" + qs.toString());
        const body = res?.data ?? res;
        this.kpis = body?.kpis || this.kpis;
        this.counts = body?.counts || this.counts;
        this.rows = Array.isArray(body?.rows) ? body.rows : [];
        this.total = Number(body?.total) || this.rows.length;
      } catch (e) {
        this.error = this.errText(e, "Could not load website visitors.");
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    async loadFlows() {
      try {
        const res = await this.api.get("/api/v1/admin/drip-flows");
        const p = res?.data?.data ?? res?.data ?? res;
        this.flows = Array.isArray(p) ? p : (p?.rows || []);
      } catch (e) { /* enrol dropdown just stays empty — not fatal */ }
    },
    setTab(key) {
      if (this.tab === key) return;
      this.tab = key;
      this.selectedMap = {};
      this.load();
    },
    async toggleExpand(companyId) {
      if (this.expanded === companyId) { this.expanded = null; return; }
      this.expanded = companyId;
      this.drill = null;
      this.drillFlowId = null;
      this.drillLoading = true;
      try {
        const res = await this.api.get("/api/v1/admin/visitor-id/matches/" + companyId);
        this.drill = res?.data ?? res;
      } catch (e) {
        this.error = this.errText(e, "Could not load visit detail.");
        this.expanded = null;
      } finally {
        this.drillLoading = false;
      }
    },
    async addToPipeline(companyId, flowId = null) {
      this.busy = true; this.error = null; this.message = "";
      try {
        const body = flowId ? { drip_flow_id: Number(flowId) } : {};
        const res = await this.api.post("/api/v1/admin/visitor-id/matches/" + companyId + "/pipeline", body);
        const ok = (res?.data ?? res)?.ok;
        if (ok) {
          this.message = flowId ? "Enrolled in the drip flow." : "Added to your pipeline.";
          await this.load();
        } else {
          this.error = (res?.data ?? res)?.error || "Could not add to pipeline.";
        }
      } catch (e) {
        this.error = this.errText(e, "Could not add to pipeline.");
      } finally {
        this.busy = false;
      }
    },
    async dismiss(companyId) {
      this.busy = true; this.error = null;
      try {
        await this.api.post("/api/v1/admin/visitor-id/matches/" + companyId + "/dismiss", {});
        this.message = "Dismissed as not relevant.";
        await this.load();
      } catch (e) {
        this.error = this.errText(e, "Could not dismiss.");
      } finally {
        this.busy = false;
      }
    },
    async bulkAction(action, flowId = null) {
      if (!this.selectedIds.length) return;
      this.busy = true; this.error = null; this.message = "";
      try {
        const body = { company_ids: this.selectedIds, action };
        if (action === "pipeline" && flowId) body.drip_flow_id = Number(flowId);
        const res = await this.api.post("/api/v1/admin/visitor-id/matches/bulk", body);
        const out = res?.data ?? res;
        this.message = `${out?.processed || 0} updated${(out?.failed || []).length ? `, ${out.failed.length} skipped (no contact)` : ""}.`;
        this.selectedMap = {};
        this.bulkFlowId = null;
        await this.load();
      } catch (e) {
        this.error = this.errText(e, "Bulk action failed.");
      } finally {
        this.busy = false;
      }
    },
    toggleOne(companyId) {
      this.selectedMap = { ...this.selectedMap, [companyId]: !this.selectedMap[companyId] };
    },
    toggleAll(ev) {
      const on = ev.target.checked;
      const map = {};
      if (on) this.rows.forEach((r) => { map[r.company_id] = true; });
      this.selectedMap = map;
    },
    async exportCsv() {
      if (this.exporting) return;
      this.exporting = true; this.error = null;
      try {
        const conf = this.filters.confidence === "high_medium" ? "" : this.filters.confidence;
        const qs = new URLSearchParams({ tab: this.tab, confidence: conf, method: this.filters.method, sector: this.filters.sector, search: this.filters.search });
        const res = await this.api.get("/api/v1/admin/visitor-id/export.csv?" + qs.toString(), { responseType: "blob" });
        const blob = res instanceof Blob ? res : (res?.data instanceof Blob ? res.data : res);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url; link.download = "website-visitors.csv";
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.error = "Bulk visitor export is included in Enterprise. Add promising visitors to a pipeline to export their engaged contacts.";
      } finally {
        this.exporting = false;
      }
    },
    // ── formatting helpers ──
    methodLabel(m) {
      return { learned: "Verified by email engagement", rdns: "Network identification (rDNS)", asn: "Network owner match (ASN)", provider: "Network provider match" }[m] || "Identified";
    },
    methodIcon(m) {
      return { learned: "envelope", rdns: "server", asn: "building" }[m] || "globe";
    },
    methodTip(m) {
      return {
        learned: "We matched this visit to a contact who has opened and clicked your emails from the same organisation.",
        rdns: "The visitor's network reverse-DNS record resolves to this company's registered domain. A strong network signal, but not a verified person.",
        asn: "The visitor's IP range is registered (ASN / WHOIS) to this organisation. Reliable for larger firms with their own network block.",
      }[m] || "";
    },
    hostOf(url) {
      if (!url) return "";
      try { return new URL(url.includes("://") ? url : "https://" + url).host.replace(/^www\./, ""); }
      catch (e) { return String(url).replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0]; }
    },
    pagePath(url) {
      if (!url) return "/";
      try { return new URL(url.includes("://") ? url : "https://x" + url).pathname || "/"; }
      catch (e) { return String(url); }
    },
    relTime(epoch) {
      if (!epoch) return "—";
      const s = Math.max(0, Math.floor(Date.now() / 1000) - Number(epoch));
      if (s < 3600) return Math.max(1, Math.floor(s / 60)) + "m ago";
      if (s < 86400) return Math.floor(s / 3600) + "h ago";
      if (s < 172800) return "Yesterday";
      if (s < 604800) return Math.floor(s / 86400) + " days ago";
      return new Date(Number(epoch) * 1000).toLocaleDateString();
    },
    fmtClock(epoch) {
      if (!epoch) return "";
      return new Date(Number(epoch) * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    sessionRange(s) {
      const d = new Date(Number(s.first_seen) * 1000);
      return d.toLocaleDateString([], { day: "numeric", month: "short" }) + ", " + this.fmtClock(s.first_seen);
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      if (body?.error === "plan_gated") return body.message || fallback;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.musubi-page--visitors { max-width: 1240px; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; }
.musubi-page__head p { margin: 0; max-width: 760px; line-height: 1.55; font-size: 0.92rem; }
.text-muted { color: var(--m-text-secondary); }

.coverage-note { display: flex; align-items: flex-start; gap: 9px; margin: 16px 0 18px; font-size: 0.8rem; color: var(--m-text-secondary); line-height: 1.5; }
.coverage-note i { color: var(--m-status-info); margin-top: 2px; }

.viz-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 8px; }
@media (max-width: 1024px) { .viz-kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .viz-kpis { grid-template-columns: 1fr; } }

.viz-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--m-border); margin: 22px 0 0; }
.viz-tab { padding: 9px 16px; font-size: 0.86rem; font-weight: 500; color: var(--m-text-secondary); border: none; background: none; border-bottom: 2px solid transparent; cursor: pointer; }
.viz-tab.active { color: var(--m-accent); border-bottom-color: var(--m-accent); font-weight: 600; }
.viz-tab .count { font-size: 0.7rem; background: var(--m-accent-soft); color: var(--m-accent-hover); padding: 1px 7px; border-radius: 999px; margin-left: 6px; font-weight: 700; }

.viz-banner { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; border-radius: 12px; background: #FFF8EC; border: 1px solid #F3E2C0; margin: 18px 0; font-size: 0.85rem; line-height: 1.5; color: #6b5320; }
.viz-banner i { color: var(--m-status-warning); margin-top: 2px; }
.viz-banner strong { color: #8A5A12; }

.viz-bulkbar { display: flex; align-items: center; gap: 12px; padding: 11px 16px; background: var(--m-accent-soft); border: 1px solid #D9DCF7; border-radius: 10px; margin: 14px 0; font-size: 0.84rem; }
.viz-bulkbar__sel { font-weight: 600; color: var(--m-accent-hover); }
.viz-bulkbar .spacer { flex: 1; }

.viz-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 16px 0; }
.viz-toolbar .spacer { flex: 1; }
.viz-select, .viz-input { border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 6px; font-size: 0.82rem; padding: 6px 10px; color: var(--m-text-primary); font-family: inherit; }
.viz-select--sm { padding: 5px 8px; font-size: 0.78rem; }

.btn { font-family: inherit; font-weight: 500; font-size: 0.82rem; padding: 0.4rem 0.85rem; border-radius: 6px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: default; }
.btn-sm { font-size: 0.76rem; padding: 0.3rem 0.6rem; }
.viz-btn-primary { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.viz-btn-primary:hover:not(:disabled) { background: var(--m-accent-hover); border-color: var(--m-accent-hover); }
.viz-btn-outline { color: var(--m-accent-hover); border-color: var(--m-accent); background: var(--m-bg-card); }
.viz-btn-outline:hover:not(:disabled) { background: var(--m-accent); color: #fff; }
.viz-btn-secondary { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.viz-btn-secondary:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }
.btn-ghost { background: none; border: none; color: var(--m-text-secondary); padding: 6px 8px; cursor: pointer; border-radius: 6px; }
.btn-ghost:hover:not(:disabled) { background: var(--m-rail-hover-bg); color: var(--m-text-primary); }
.viz-inpipe { display: inline-flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 600; color: var(--m-status-success); }

.viz-loading { padding: 32px; text-align: center; color: var(--m-text-secondary); }

.viz-tbl { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg); overflow: hidden; }
.viz-tbl table { width: 100%; border-collapse: collapse; }
.viz-tbl thead th { background: rgba(79, 70, 229, 0.07); border-bottom: 1px solid var(--m-border); text-align: left; font-size: 0.66rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--m-text-secondary); padding: 11px 14px; white-space: nowrap; }
.viz-tbl tbody td { padding: 0.7rem 0.85rem; font-size: 0.85rem; border-bottom: 1px solid var(--m-border); vertical-align: middle; }
.viz-tbl tbody tr.is-open { background: rgba(79, 70, 229, 0.05); }
.checkcell { width: 34px; text-align: center; }
.co-name { font-weight: 600; color: var(--m-text-primary); }
.co-web { font-size: 0.76rem; color: var(--m-text-secondary); display: flex; align-items: center; gap: 5px; margin-top: 2px; }
.co-web i { color: var(--m-text-tertiary); }
.chev { background: none; border: none; color: var(--m-text-tertiary); cursor: pointer; padding: 2px; display: inline-flex; transition: transform 0.15s; }
tr.is-open .chev { transform: rotate(90deg); color: var(--m-accent); }
.chip { display: inline-block; padding: 0.16rem 0.55rem; font-size: 0.72rem; font-weight: 600; border-radius: 5px; background: #EEF1F6; color: #556074; white-space: nowrap; }
.conf { display: inline-flex; align-items: center; gap: 5px; padding: 0.16rem 0.55rem; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; border-radius: 5px; white-space: nowrap; }
.conf.high { background: #DDEFE3; color: #2F7A4D; }
.conf.high .dot { width: 6px; height: 6px; border-radius: 50%; background: #2F7A4D; }
.conf.medium { background: var(--m-bg-card); color: #8A6D1F; border: 1px solid #E7D9A8; }
.conf.medium .dot { width: 6px; height: 6px; border-radius: 50%; border: 1.5px solid #B8952F; }
.method { display: inline-flex; align-items: center; gap: 6px; font-size: 0.78rem; color: var(--m-text-secondary); cursor: help; }
.method i { color: var(--m-text-tertiary); }
.pv-count { font-weight: 600; font-variant-numeric: tabular-nums; }
.recency { font-size: 0.82rem; }
.recency i { color: var(--m-text-tertiary); margin-right: 4px; }
.row-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }

.drill { background: #FBFCFE; }
.drill__loading { padding: 20px 26px; color: var(--m-text-secondary); }
.drill__inner { display: grid; grid-template-columns: 1.3fr 1fr; gap: 26px; padding: 22px 26px; }
@media (max-width: 900px) { .drill__inner { grid-template-columns: 1fr; } }
.drill h4 { margin: 0 0 12px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--m-text-secondary); }
.tl-block + .tl-block { margin-top: 8px; }
.tl-session { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--m-text-tertiary); margin: 4px 0 10px; }
.timeline { position: relative; padding-left: 20px; }
.timeline::before { content: ""; position: absolute; left: 5px; top: 4px; bottom: 4px; width: 2px; background: var(--m-border-strong); }
.tl-item { position: relative; padding: 0 0 16px; }
.tl-item::before { content: ""; position: absolute; left: -18px; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: var(--m-bg-card); border: 2px solid var(--m-accent); }
.tl-item.session-start::before { background: var(--m-accent); }
.tl-page { font-weight: 600; font-size: 0.85rem; }
.tl-meta { font-size: 0.75rem; color: var(--m-text-secondary); margin-top: 2px; }
.explain { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 10px; padding: 14px 16px; font-size: 0.85rem; line-height: 1.55; margin-bottom: 16px; }
.explain .lead { font-weight: 600; display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.explain .lead i { color: var(--m-status-success); }
.facts { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 18px; margin-bottom: 16px; }
.fact__k { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 600; margin-bottom: 2px; }
.fact__v { font-weight: 600; font-size: 0.82rem; }
.fact__v.corpus { display: inline-flex; align-items: center; gap: 5px; }
.corpus-tag { font-size: 0.6rem; font-weight: 700; color: var(--m-accent-2); background: rgba(14, 165, 164, 0.1); padding: 1px 5px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.03em; }
.drill__cta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

.dc-empty { text-align: center; padding: 48px 24px; color: var(--m-text-secondary); background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg); }
.dc-empty__icon { display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px; border-radius: 50%; background: var(--m-accent-soft); color: var(--m-accent-hover); font-size: 24px; margin-bottom: 16px; }
.dc-empty__title { font-weight: 600; color: var(--m-text-primary); font-size: 1rem; margin-bottom: 8px; }
.dc-empty__hint { font-size: 0.86rem; max-width: 520px; margin: 0 auto 8px; line-height: 1.6; }
.dc-empty__why { max-width: 520px; margin: 14px auto 0; text-align: left; display: grid; gap: 8px; }
.dc-empty__why .row { display: flex; gap: 9px; align-items: flex-start; font-size: 0.82rem; line-height: 1.45; }
.dc-empty__why .row i { color: var(--m-status-info); margin-top: 3px; }
.dc-empty__actions { margin-top: 22px; display: flex; gap: 10px; justify-content: center; }

/* Violet Starter teaser */
.viz-teaser { position: relative; border-radius: var(--m-radius-lg); overflow: hidden; border: 1px solid var(--m-border); margin: 20px 0; min-height: 340px; }
.viz-teaser__blur { filter: blur(4px); opacity: 0.55; pointer-events: none; user-select: none; padding: 8px; }
.viz-skel-tbl { background: var(--m-bg-card); }
.viz-skel-row { display: flex; gap: 18px; align-items: center; padding: 16px 18px; border-bottom: 1px solid var(--m-border); }
.skel { height: 11px; border-radius: 5px; background: linear-gradient(90deg, #E9ECF3, #F3F5F9, #E9ECF3); display: inline-block; }
.viz-teaser__panel { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, rgba(243, 238, 254, 0.72), rgba(251, 250, 255, 0.82)); }
.upgrade { max-width: 520px; text-align: center; background: linear-gradient(135deg, var(--m-violet-soft, #F3EEFE), #FBFAFF); border: 1px solid var(--m-violet-line, #E0D2FB); border-radius: 16px; padding: 30px 34px; box-shadow: var(--m-shadow-panel); }
.upgrade__ic { width: 52px; height: 52px; border-radius: 14px; background: var(--m-bg-card); border: 1px solid var(--m-violet-line, #E0D2FB); color: var(--m-violet, #7C3AED); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 14px; }
.upgrade__eyebrow { font-size: 0.68rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--m-violet, #7C3AED); margin-bottom: 8px; }
.upgrade h3 { margin: 0 0 8px; font-size: 1.25rem; }
.upgrade p { margin: 0 0 18px; font-size: 0.88rem; color: var(--m-text-secondary); line-height: 1.55; }
.upgrade__benefits { text-align: left; display: grid; gap: 10px; margin: 0 auto 22px; max-width: 390px; }
.benefit { display: flex; gap: 10px; align-items: flex-start; font-size: 0.86rem; line-height: 1.4; }
.benefit i { color: var(--m-violet, #7C3AED); margin-top: 3px; }
.upgrade__actions { display: flex; gap: 10px; justify-content: center; align-items: center; }
.viz-btn-violet { background: var(--m-violet, #7C3AED); border-color: var(--m-violet, #7C3AED); color: #fff; }
.viz-btn-violet:hover { background: #6D28D9; }
.viz-compare { font-size: 0.85rem; font-weight: 700; color: var(--m-violet, #7C3AED); text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
.viz-compare:hover { text-decoration: underline; }
</style>
